import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Highlighter from "react-highlight-words";
import { SearchOutlined, PlusOutlined } from "@ant-design/icons";
import { Table, Button, Space, Tooltip, Input } from "antd";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useSelector } from "react-redux";

function PaymentConditionList({
  conditions,
  loading,
  openDeleteModal,
}) {
  const navigate = useNavigate();
  const searchInput = useRef(null);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const user = useSelector((state) => state.user);

  const canCreate = user?.permissoes?.paymentCondition?.isCreate === true;
  const canEdit = user?.permissoes?.paymentCondition?.isEdit === true;
  const canDelete = user?.permissoes?.paymentCondition?.isDelete === true;
  const canView = user?.permissoes?.paymentCondition?.isView === true;
  const showActionsColumn = canEdit || canDelete || canView;

  const handleSearch = (selectedKeys, confirm, columnKey) => {
    const value = selectedKeys?.[0] || "";
    confirm();
    setSearchText(value);
    setSearchedColumn(columnKey);
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]: value,
    }));
  };

  const handleReset = (clearFilters, columnKey, confirm) => {
    clearFilters && clearFilters();
    setSearchText("");
    setSearchedColumn("");
    setColumnFilters((prev) => {
      const updated = { ...prev };
      delete updated[columnKey];
      return updated;
    });
    confirm && confirm();
  };

  const getColumnSearchProps = (dataIndex) => {
    const columnKey = Array.isArray(dataIndex) ? dataIndex.join(".") : dataIndex;

    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys = [], confirm, clearFilters, close }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder="Buscar"
            value={selectedKeys[0] ?? ""}
            onChange={(e) => {
              const value = e.target.value ?? "";
              setSelectedKeys(value ? [value] : []);
            }}
            onPressEnter={() => handleSearch(selectedKeys, confirm, columnKey)}
            style={{ marginBottom: 8, display: "block" }}
          />

          <Space>
            <Button
              type="primary"
              size="small"
              icon={<SearchOutlined />}
              style={{ width: 90 }}
              onClick={() => handleSearch(selectedKeys, confirm, columnKey)}
            >
              Buscar
            </Button>

            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => handleReset(clearFilters, columnKey, confirm)}
            >
              Reiniciar
            </Button>

            <Button type="link" size="small" onClick={() => close()}>
              Fechar
            </Button>
          </Space>
        </div>
      ),

      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),

      filteredValue: columnFilters[columnKey] ? [columnFilters[columnKey]] : null,

      onFilter: (value, record) => {
        const text = record[dataIndex];
        return text
          ? text.toString().toLowerCase().includes(value.toLowerCase())
          : false;
      },

      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      },

      render: (text) =>
        searchedColumn === columnKey ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text ? text.toString() : ""}
          />
        ) : (
          text
        ),
    };
  };

  const dataSource = (conditions || []).map((item) => ({
    ...item,
    activeLabel: item.active ? "Sim" : "Não",
  }));

  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: "Descricao",
        dataIndex: "description",
        key: "description",
        ...getColumnSearchProps("description"),
        sorter: (a, b) => (a.description || "").localeCompare(b.description || ""),
      },
      {
        title: "Observacoes",
        dataIndex: "observation",
        key: "observation",
        ellipsis: true,
        ...getColumnSearchProps("observation"),
        sorter: (a, b) => (a.observation || "").localeCompare(b.observation || ""),
      },
      {
        title: "Ativo",
        dataIndex: "activeLabel",
        key: "active",
        align: "center",
        ...getColumnSearchProps("activeLabel"),
        sorter: (a, b) => Number(a.active) - Number(b.active),
      },
    ];

    if (!showActionsColumn) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        title: "Acoes",
        key: "acoes",
        align: "start",
        render: (_, record) => (
          <Space>
            {canEdit ? (
              <Tooltip title="Editar">
                <Button
                  type="link"
                  icon={<EditOutlinedIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/paymentCondition/edit/${record.id}`);
                  }}
                />
              </Tooltip>
            ) : canView ? (
              <Tooltip title="Visualizar">
                <Button
                  type="link"
                  icon={<VisibilityIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/paymentCondition/edit/${record.id}`, {
                      state: { viewOnly: true },
                    });
                  }}
                />
              </Tooltip>
            ) : null}

            {canDelete ? (
              <Tooltip title="Excluir">
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlinedIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteModal({
                      id: record.id,
                      description: record.description,
                    });
                  }}
                />
              </Tooltip>
            ) : null}
          </Space>
        ),
      },
    ];
  }, [
    canDelete,
    canEdit,
    canView,
    columnFilters,
    navigate,
    openDeleteModal,
    searchText,
    searchedColumn,
    showActionsColumn,
  ]);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
        }}
      >
        <h2 style={{ color: "var(--fortio-primary)", margin: 0 }}>
          Listagem de Condicoes de Pagamento
        </h2>

        {canCreate ? (
          <Button
            id="ButtonHover"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/paymentCondition/new")}
            style={{ marginRight: 200 }}
          >
            Novo
          </Button>
        ) : null}
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={{ pageSize: 10 }}
      />
    </>
  );
}

export default PaymentConditionList;
